import fb from '@/firebaseConfig';
import router from '@/router';
import analytics from '@/utils/Analytics';
import localStore from '@/utils/LocalStore';

/*

  STATE

 */

const state = {
  routeName: '',
  routeParam: '',
  routeMeta: {},
  currentId: '',
  currentSet: [],
  currentSetName: '',
  currentIndex: null,
  parentSet: [],
  parentSetName: '',
  parentIndex: null,
  snack: {
    text: '',
    actionText: '',
    handler: null,
    keepOpen: false,
    quick: false,
  },
  paywall: '',
  isAudioDefaultMode: false, // Whether the user last toggled to audio or text
};

/*

  GETTERS

 */

const getters = {
  routeLoaded: state => state.routeName !== '' && state.routeName !== null,
  isLanding: state => state.routeName === 'Landing',
  isContentView: state => !!state.routeMeta.isContentView,
  isAudioView: state => !!state.routeMeta.isAudioView,
  isDailyCardView: state => state.currentSetName === 'dailyCards',
  // isStackView: state => state.routeName === 'Stack' || state.routeName === 'AudioStack',
  // isCardInStack: state => state.currentSetName.indexOf('StackCards') > 0,

  currentCategory: (state, getters, rootState) => {
    const { routeName, routeParam, currentId } = state;
    const currentCard = rootState.cards.allCards[currentId];
    let category = 'journey';
    if (routeName === 'Category') {
      category = routeParam;
    } else if ((routeName === 'Card' || routeName === 'AudioCard') && currentCard) {
      category = currentCard.category; // eslint-disable-line
    }
    return category;
  },

  // Is the view a premium feature (regardless of content)
  isPremiumView: state => !!state.routeMeta.isPremium,

  // Is the content marked premium
  isPremiumContent: (state, getters, rootState, rootGetters) => {
    const { routeName, currentId } = state;
    if (routeName === 'Card' || routeName === 'AudioCard') {
      const card = rootState.cards.allCards[currentId];
      return card && card.isPremium;
    }
    if (routeName === 'Stack' || routeName === 'AudioStack') {
      const stack = rootGetters['stacks/allStacks'][currentId];
      return stack && stack.isPremium;
    }
    return false;
  },

  nextIndex: state => {
    const { currentSet = [], currentIndex = 0 } = state;
    return currentIndex === currentSet.length - 1 ? 0 : currentIndex + 1;
  },
  nextId: (state, getters) => {
    return state.currentSet[getters.nextIndex];
  },
  previousIndex: state => {
    const { currentSet = [], currentIndex = 0 } = state;
    return currentIndex === 0 ? currentSet.length - 1 : currentIndex - 1;
  },
  previousId: (state, getters) => {
    const { currentSet = [] } = state;
    return currentSet[getters.previousIndex];
  },
};

/*

  ACTIONS

 */

const actions = {
  init({ dispatch }) {
    dispatch('getIsAudioDefaultMode');
  },

  parseRoute({ commit, dispatch, state, getters }, { name, params, meta }) {
    const oldMeta = state.routeMeta;

    let param = '';

    if (name === 'Category') {
      param = params.category || '';
    } else if (meta.isContentView) {
      param = params.id || '';
      commit('setCurrentId', param);
      if (state.currentIndex === null) {
        dispatch('getSavedSet', param);
      }
    }

    commit('setCurrentRoute', { name, param, meta });

    if (name === 'Card' || name === 'AudioCard') {
      dispatch('cards/addToRecentIds', param, { root: true });
    }

    if (name === 'Stack' || name === 'AudioStack') {
      dispatch('updateStackSet');
      dispatch('stacks/addToRecentIds', param, { root: true });
    }

    const { isAudioView } = meta;
    // Coming from an AudioView to an audio or non-audio route
    if (oldMeta.isAudioView) {
      dispatch('audio/handleRouteChange', { isAudioView, routeName: name }, { root: true });
    }

    const { currentCategory, isPremiumContent } = getters;
    analytics.setCurrentScreen(name, param, currentCategory, isPremiumContent);

    // console.log('$route', name, param);
  },

  handleNativeDeepLink({ dispatch }, url) {
    console.log('handleNativeDeepLink:', url);

    const isSignInLink = fb.auth().isSignInWithEmailLink(url);
    if (isSignInLink) {
      dispatch('auth/handleSignInWithEmailLink', url, { root: true });
    } else if (url.indexOf('auth/action?') > -1) {
      dispatch('auth/handleAuthActionLink', url, { root: true });
    } else {
      const slug = url.split('link.imaginarie.app').pop();
      if (slug) {
        router.push(slug);
      }
    }
  },

  updateCurrentSet({ commit }, { set, setName, index }) {
    if (set) {
      commit('setCurrentSet', { set, setName, index });
      // Save current set in case of page refresh
      localStore.setItem('currentSet', { set, setName });
    }
  },

  // When Content views can be multiple levels, track Parent set in addition to Current Set
  // ex: After viewing card from stack, can go back to swipe through other Stacks
  // Called from Stacks-View when a stack is clicked
  // Not saving in localStore because router doesn't remember previous route
  updateParentSet({ commit }, { set, setName, index }) {
    if (set) {
      commit('setParentSet', { set, setName, index });
    }
  },

  // When coming from Stacks View, parent set has already been set to Stack set
  // When coming from Card within Stack, can use parent set to get back Stack set
  updateStackSet({ state, dispatch }) {
    const { parentSet: set, parentSetName: setName, parentIndex: index } = state;
    if (index === null) {
      dispatch('updateCurrentSet', {
        set: [state.currentId],
        setName: '',
        index: 0,
      });
    } else {
      dispatch('updateCurrentSet', { set, setName, index });
    }
  },

  clearCurrentSet({ commit, dispatch }) {
    dispatch('updateCurrentSet', {
      set: [''],
      setName: '',
      index: 0,
    });
    commit('setCurrentId', '');
  },

  async getSavedSet({ dispatch }, id) {
    if (!id) {
      dispatch('clearCurrentSet');
      return;
    }
    const savedSet = await localStore.getItem('currentSet'); // returns null if doesn't exist
    if (savedSet) {
      const { set, setName } = savedSet;
      const index = set.indexOf(id);
      if (index > -1) {
        dispatch('updateCurrentSet', { set, setName, index });
        return;
      }
    }
    dispatch('updateCurrentSet', { set: [id], setName: '', index: 0 });
  },

  updateSnack(
    { commit },
    { text = '', actionText = '', handler = null, keepOpen = false, quick = false }
  ) {
    commit('setSnack', { text, actionText, handler, keepOpen, quick });
  },

  // Track feature user tried to access when shown paywall
  showPaywall({ commit }, feature) {
    commit('setPaywall', feature);
  },

  hidePaywall({ commit }) {
    commit('setPaywall', '');
  },

  async getIsAudioDefaultMode({ commit }) {
    const isAudioDefaultMode = await localStore.getItem('isAudioDefaultMode'); // returns null if doesn't exist
    commit('setIsAudioDefaultMode', !!isAudioDefaultMode);
  },

  updateIsAudioDefaultMode({ commit }, bool) {
    commit('setIsAudioDefaultMode', bool);
    localStore.setItem('isAudioDefaultMode', bool);
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setCurrentRoute(state, { name, param, meta }) {
    state.routeName = name;
    state.routeParam = param;
    state.routeMeta = meta;
  },

  setCurrentId(state, id) {
    state.currentId = id;
  },

  setCurrentSet(state, { set, setName, index }) {
    state.currentSet = set;
    state.currentSetName = setName;
    state.currentIndex = index;
  },

  setParentSet(state, { set, setName, index }) {
    state.parentSet = set;
    state.parentSetName = setName;
    state.parentIndex = index;
  },

  setCurrentIndex(state, index) {
    const { routeName } = state;
    state.currentIndex = index;
    if (routeName === 'Stack' || routeName === 'AudioStack') {
      state.parentIndex = index;
    }
  },

  setCurrentIndexForId(state, id) {
    const { currentSet } = state;
    const index = currentSet.indexOf(id);
    state.currentIndex = index > -1 ? index : null;
  },

  setSnackText(state, text) {
    state.snackText = text;
  },

  setSnack(state, snack) {
    state.snack = snack;
  },

  setPaywall(state, feature = '') {
    state.paywall = feature;
  },

  setIsAudioDefaultMode(state, bool) {
    state.isAudioDefaultMode = bool;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
